import * as ReactDOM from 'react-dom/client';

import { APP_DEGRADED_ATTRIBUTE } from 'shared/constants/hydration';
import { Application } from 'shared/containers/App';
import { type IApplicationContext } from 'shared/types/applicationContext';
import { type TReduxStore } from 'shared/types/redux';
import { toError } from 'shared/utils/toError';

interface IDependencies {
  store: TReduxStore;
  context: IApplicationContext;
}

export function renderApplication(dependencies: IDependencies): void {
  const { store, context } = dependencies;
  const { config, logger } = context;

  const projectName = config.getStrict<string>('projectName');

  const rootElement = document.getElementById(projectName);

  if (!rootElement) {
    throw new Error(`Unable to find element #${projectName}`);
  }

  /**
   * Снимок серверной разметки до гидрации. React 19 при ошибке гидрации очищает root и перерендеривает
   * его на клиенте, поэтому снять снимок можно только здесь. Используется как заглушка корневого
   * error boundary и для восстановления после uncaught error.
   */
  const ssrMarkup = rootElement.innerHTML;

  ReactDOM.hydrateRoot(rootElement, <Application context={context} reduxStore={store} ssrMarkup={ssrMarkup} />, {
    // Ошибка поймана error boundary (корневым или блока). Алерты настраиваются по domain
    onCaughtError: (error, errorInfo) => {
      logger.error(toError(error), { componentStack: errorInfo.componentStack, domain: 'react.caughtError' });
    },
    // Hydration mismatch и ошибки гидрации, после которых React сам перерендерил дерево на клиенте
    onRecoverableError: (error, errorInfo) => {
      logger.warning(toError(error), { componentStack: errorInfo.componentStack, domain: 'react.recoverableError' });
    },
    // Ошибка не поймана ни одним boundary: React размонтировал root. Возвращаем серверную разметку,
    // чтобы страница не осталась пустой: интерактивность теряется, контент для индексации остаётся
    onUncaughtError: (error, errorInfo) => {
      logger.error(toError(error), { componentStack: errorInfo.componentStack, domain: 'react.uncaughtError' });
      rootElement.innerHTML = ssrMarkup;
      document.documentElement.setAttribute(APP_DEGRADED_ATTRIBUTE, 'true');
    },
  });
}
